import { api } from "@/api/client";
import type {
  MulticheckAccuracyStat,
  MulticheckCoverage,
  MulticheckTimeStat,
} from "@/api/types";
import { InfoDot, PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatDurationSeconds, formatInt, formatPercent, modeLabel } from "@/lib/format";
import { includeTestSessions, selectedTenant, signalsWindow } from "@/state/filters";

// Behavioural + safety signals captured per turn / per session but previously
// not shown anywhere: abuse probes (L1/L2), R7 self-elimination, sub-flow
// (multi-intent / trainer topics / Multicheck variant), and session behaviour
// (source clicks, mode switches). On-the-fly rollup with a window selector.
export function SignalsView() {
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) =>
      api.signals(
        {
          window: signalsWindow.value,
          includeTest: includeTestSessions.value,
          tenant: selectedTenant.value,
        },
        s,
      ),
    [signalsWindow.value, includeTestSessions.value, selectedTenant.value],
  );

  const safety = data?.safety;

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness lastFetched={lastFetched} loading={loading} error={error} onRefresh={refetch} />
      </div>

      <section class="card">
        <h2>{t("signals.safety")}</h2>
        <PanelHelp k="signals.safety" />
        <div style="display: flex; gap: 32px; flex-wrap: wrap; margin-bottom: 12px;">
          <Kpi label={t("signals.turnsInWindow")} value={safety ? formatInt(safety.total_turns) : "—"} />
          <Kpi
            label={t("signals.offTopic")}
            value={
              safety
                ? `${formatInt(safety.off_topic_attempts)} (${formatPercent(safety.off_topic_rate)})`
                : "—"
            }
          />
          <Kpi
            label={t("signals.attacks")}
            value={
              safety
                ? `${formatInt(safety.system_prompt_attacks)} (${formatPercent(safety.attack_rate)})`
                : "—"
            }
          />
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px;">
          <DistTable
            title={t("signals.offTopicByMode")}
            data={safety?.off_topic_by_mode}
            keyHeader={t("common.mode")}
            keyLabel={modeLabel}
          />
          <DistTable
            title={t("signals.attacksByMode")}
            data={safety?.attack_by_mode}
            keyHeader={t("common.mode")}
            keyLabel={modeLabel}
          />
        </div>
      </section>

      <section class="card">
        <h2>
          {t("signals.selfElimination")}{" "}
          <span class="muted">{t("signals.selfEliminationSub")}</span>{" "}
          <InfoDot k="signals.selfElimination" />
        </h2>
        <PanelHelp k="signals.selfElimination" />
        <DistTable title="" data={data?.self_elimination} keyHeader={t("common.reason")} />
      </section>

      <section class="card">
        <h2>{t("signals.subflow")}</h2>
        <PanelHelp k="signals.subflow" />
        <div style="display: flex; gap: 32px; flex-wrap: wrap; margin-bottom: 12px;">
          <Kpi
            label={t("signals.multiIntent")}
            value={
              data
                ? `${formatInt(data.multi_intent_turns)} (${formatPercent(data.multi_intent_rate)})`
                : "—"
            }
          />
          <Kpi label={t("signals.geoRadius")} value={data ? formatInt(data.geo_radius_turns) : "—"} />
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px;">
          <DistTable
            title={t("signals.trainerTopics")}
            data={data?.trainer_interview_topics}
            keyHeader={t("signals.topic")}
          />
          <DistTable
            title={t("signals.mcVariants")}
            data={data?.multicheck_variants}
            keyHeader={t("signals.variant")}
          />
          <DistTable
            title={t("signals.mcSubtests")}
            data={data?.multicheck_subtests}
            keyHeader={t("signals.subtest")}
          />
        </div>
      </section>

      <section class="card">
        <h2>{t("signals.zeit")}</h2>
        <PanelHelp k="signals.zeittraining" />
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px;">
          <TimesTable
            title={t("signals.timesBySubtest")}
            data={data?.multicheck_time_by_subtest}
            keyHeader={t("signals.subtest")}
          />
          <TimesTable
            title={t("signals.timesByVariant")}
            data={data?.multicheck_time_by_variant}
            keyHeader={t("signals.variant")}
          />
        </div>
      </section>

      <section class="card">
        <h2>{t("signals.results")}</h2>
        <PanelHelp k="signals.trainingsergebnisse" />
        <CoverageNote coverage={data?.multicheck_coverage} />
        {/* Stacked, not side by side: the outcome breakdown needs the width,
            and a first-attempt count without it is the figure that misled. */}
        <div style="display: grid; gap: 24px;">
          <AccuracyTable
            title={t("signals.accBySubtest")}
            data={data?.multicheck_accuracy_by_subtest}
            keyHeader={t("signals.subtest")}
          />
          <AccuracyTable
            title={t("signals.accByThema")}
            data={data?.multicheck_accuracy_by_thema}
            keyHeader={t("signals.topic")}
          />
          {data?.multicheck_legacy_accuracy ? (
            <AccuracyTable
              title={t("signals.legacyTitle")}
              data={{ [t("signals.legacyRow")]: data.multicheck_legacy_accuracy }}
              keyHeader={t("signals.record")}
            />
          ) : null}
        </div>
      </section>

      <section class="card">
        <h2>{t("signals.sessionBehaviour")}</h2>
        <PanelHelp k="signals.sessionBehaviour" />
        <div style="display: flex; gap: 32px; flex-wrap: wrap; margin-bottom: 12px;">
          <Kpi label={t("signals.sessionsInWindow")} value={data ? formatInt(data.total_sessions) : "—"} />
          <Kpi label={t("signals.sourceClicks")} value={data ? formatInt(data.source_clicks_total) : "—"} />
          <Kpi
            label={t("signals.continued")}
            value={data ? formatInt(data.continued_after_no_answer_sessions) : "—"}
          />
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px;">
          <DistTable
            title={t("signals.topClicked")}
            data={data?.top_clicked_professions}
            keyHeader={t("quality.profession")}
          />
          <DistTable
            title={t("signals.modeSwitches")}
            data={data?.mode_switch_distribution}
            keyHeader={t("signals.switches")}
            sort={false}
          />
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div class="muted" style="font-size: 12px;">
        {label}
      </div>
      <div style="font-size: 22px; font-weight: 600;">{value}</div>
    </div>
  );
}

function TimesTable({
  title,
  data,
  keyHeader,
}: {
  title: string;
  data: Record<string, MulticheckTimeStat> | undefined;
  keyHeader: string;
}) {
  const entries = Object.entries(data ?? {});
  entries.sort((a, b) => b[1].count - a[1].count);
  return (
    <div>
      <h3 style="font-size: 13px; margin: 0 0 8px;">{title}</h3>
      <table class="table">
        <thead>
          <tr>
            <th>{keyHeader}</th>
            <th style="text-align: right;">{t("signals.n")}</th>
            <th style="text-align: right;">{t("signals.avg")}</th>
            <th style="text-align: right;">{t("engagement.median")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={4} class="muted">
                {t("signals.noTimed")}
              </td>
            </tr>
          ) : (
            entries.map(([k, s]) => (
              <tr key={k}>
                <td>{k}</td>
                <td style="text-align: right;">{formatInt(s.count)}</td>
                <td style="text-align: right;">{formatDurationSeconds(s.avg_seconds)}</td>
                <td style="text-align: right;">{formatDurationSeconds(s.median_seconds)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Says so when the rollup below is a sample rather than the whole window.
 *
 * Both history reads are bounded (sessions, and turns per session). Silence
 * used to make a bounded statistic look like a complete one, and a truncated
 * history understates: it loses its newest turns, so an exercise can still
 * look open and then counts nowhere. */
function CoverageNote({ coverage }: { coverage: MulticheckCoverage | undefined }) {
  if (!coverage) return null;
  const bounded = coverage.sessions_omitted > 0 || coverage.sessions_truncated > 0;
  if (!bounded) return null;
  return (
    <p class="muted" style="font-size: 12px; margin: 0 0 12px;">
      {t("signals.coverageBounded")} {t("signals.coverageSessions")}:{" "}
      {formatInt(coverage.sessions_considered)}, {t("signals.coverageOmitted")}:{" "}
      {formatInt(coverage.sessions_omitted)}, {t("signals.coverageTruncated")}:{" "}
      {formatInt(coverage.sessions_truncated)}.
    </p>
  );
}

/** The outcome columns. `correct` is «right on the FIRST attempt», so showing
 * it alone made a task solved on the second attempt read as zero correct with
 * nothing to explain it. These four partition `tasks`; skipped sits outside it. */
function AccuracyTable({
  title,
  data,
  keyHeader,
}: {
  title: string;
  data: Record<string, MulticheckAccuracyStat> | undefined;
  keyHeader: string;
}) {
  // Weakest first — that's the "hier lohnt sich mehr Training" reading order.
  const entries = Object.entries(data ?? {});
  entries.sort((a, b) => a[1].correct_rate - b[1].correct_rate);
  return (
    <div>
      <h3 style="font-size: 13px; margin: 0 0 8px;">{title}</h3>
      <table class="table">
        <thead>
          <tr>
            <th>{keyHeader}</th>
            <th style="text-align: right;">{t("signals.tasks")}</th>
            <th style="text-align: right;">{t("signals.firstTry")}</th>
            <th style="text-align: right;">{t("signals.afterHelp")}</th>
            <th style="text-align: right;">{t("signals.revealed")}</th>
            <th style="text-align: right;">{t("signals.unsolved")}</th>
            <th style="text-align: right;">{t("signals.skippedTasks")}</th>
            <th style="text-align: right;">{t("signals.rate")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={8} class="muted">
                {t("signals.noGraded")}
              </td>
            </tr>
          ) : (
            entries.map(([k, s]) => (
              <tr key={k}>
                <td>{k}</td>
                <td style="text-align: right;">{formatInt(s.tasks)}</td>
                <td style="text-align: right;">{formatInt(s.correct)}</td>
                <td style="text-align: right;">{formatInt(s.after_help ?? 0)}</td>
                <td style="text-align: right;">{formatInt(s.revealed ?? 0)}</td>
                <td style="text-align: right;">{formatInt(s.unsolved ?? 0)}</td>
                <td style="text-align: right;">{formatInt(s.skipped ?? 0)}</td>
                <td style="text-align: right;">{formatPercent(s.correct_rate)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function DistTable({
  title,
  data,
  keyHeader,
  keyLabel = (k: string) => k,
  sort = true,
}: {
  title: string;
  data: Record<string, number> | undefined;
  keyHeader: string;
  keyLabel?: (key: string) => string;
  sort?: boolean;
}) {
  const entries = Object.entries(data ?? {});
  if (sort) entries.sort((a, b) => b[1] - a[1]);
  return (
    <div>
      {title && <h3 style="font-size: 13px; margin: 0 0 8px;">{title}</h3>}
      <table class="table">
        <thead>
          <tr>
            <th>{keyHeader}</th>
            <th style="text-align: right;">{t("common.count")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={2} class="muted">
                {t("signals.noneInWindow")}
              </td>
            </tr>
          ) : (
            entries.map(([k, n]) => (
              <tr key={k}>
                <td>{keyLabel(k)}</td>
                <td style="text-align: right;">{formatInt(n)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
